import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { getAssignments, subscribeToJobUpdates, getRequiredQuotes } from "../../lib/supabase/assignments";

import {
    FiCalendar, FiUser, FiPhone, FiTool, FiAlertCircle, FiEye,
    FiFilter, FiMail, FiPhoneOff, FiClock, FiCheckCircle,
    FiEdit3, FiDollarSign, FiClipboard, FiFlag, FiSearch, FiPackage
} from "react-icons/fi";
import { hasPermission } from "../../lib/permissions";

const Assignments = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();

    const [assignments, setAssignments] = useState([]);
    const [filteredAssignments, setFilteredAssignments] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterJobType, setFilterJobType] = useState("all");
    const [sortBy, setSortBy] = useState("newest");
    const [lastUpdated, setLastUpdated] = useState(new Date());

    useEffect(() => {
        if (company?.id) {
            fetchAssignments();
        }
    }, [company]);

    const fetchAssignments = async () => {
        try {
            const data = await getAssignments(company.id);
            setAssignments(data);
            setFilteredAssignments(data);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Error fetching assignments:", error);
            alert("Error fetching assignments");
        }
    };

    // Real-time subscription
    useEffect(() => {
        if (!company?.id) return;

        const channel = subscribeToJobUpdates(company.id, (payload) => {
            console.log('Real-time update:', payload);
            fetchAssignments();
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [company?.id]);

    // Filter and search logic
    useEffect(() => {
        let filtered = [...assignments];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(
                (assignment) =>
                    assignment.unique_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    assignment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    assignment.carpenter_supervisor_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Status filter
        if (filterStatus !== "all") {
            filtered = filtered.filter((assignment) => assignment.main_status === filterStatus);
        }

        // Job type filter
        if (filterJobType !== "all") {
            filtered = filtered.filter((assignment) => assignment.job_type === filterJobType);
        }

        // Sort
        switch (sortBy) {
            case "newest":
                filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case "oldest":
                filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                break;
            case "priority":
                filtered.sort((a, b) => {
                    const aPriority = a.urgent_quote || a.urgent_design;
                    const bPriority = b.urgent_quote || b.urgent_design;
                    return bPriority - aPriority;
                });
                break;
            default:
                break;
        }

        setFilteredAssignments(filtered);
    }, [assignments, searchTerm, filterStatus, filterJobType, sortBy]);

    const getStatusColor = (status) => {
        const statusColors = {
            pending_design: "bg-amber-50 text-amber-700 border-amber-200",
            in_design: "bg-blue-50 text-blue-700 border-blue-200",
            design_completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
            pending_estimation: "bg-orange-50 text-orange-700 border-orange-200",
            in_estimation: "bg-purple-50 text-purple-700 border-purple-200",
            estimation_completed: "bg-green-50 text-green-700 border-green-200",
            ready_for_delivery: "bg-teal-50 text-teal-700 border-teal-200",
            completed: "bg-gray-50 text-gray-700 border-gray-200"
        };
        return statusColors[status] || "bg-gray-50 text-gray-700 border-gray-200";
    };

    const getStatusIcon = (status) => {
        const icons = {
            pending_design: <FiClock className="w-3.5 h-3.5" />,
            in_design: <FiEdit3 className="w-3.5 h-3.5" />,
            design_completed: <FiCheckCircle className="w-3.5 h-3.5" />,
            pending_estimation: <FiClipboard className="w-3.5 h-3.5" />,
            in_estimation: <FiDollarSign className="w-3.5 h-3.5" />,
            estimation_completed: <FiCheckCircle className="w-3.5 h-3.5" />,
            ready_for_delivery: <FiPackage className="w-3.5 h-3.5" />,
            completed: <FiCheckCircle className="w-3.5 h-3.5" />
        };
        return icons[status] || <FiClock className="w-3.5 h-3.5" />;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatRelativeTime = (date) => {
        if (!date) return 'Unknown time';

        const now = new Date();
        const inputDate = new Date(date);
        const diffMs = now - inputDate;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMinutes < 1) return 'just now';
        if (diffMinutes < 60) return `${diffMinutes}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return formatDate(date);
    };

    const handleViewAssignment = (assignmentId) => {
        navigate(`/${companySlug}/assignment/${assignmentId}`);
    };

    const FilterButton = ({ label, isSelected, onClick, count }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-lg border transition-all duration-200 font-medium text-sm flex items-center gap-2 ${isSelected
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
        >
            {label}
            {count !== undefined && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${isSelected ? 'bg-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                    {count}
                </span>
            )}
        </button>
    );

    return (
        <>
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Assignments</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Last updated {formatRelativeTime(lastUpdated)}
                        </p>
                    </div>
                    {hasPermission(userProfile?.role, ["app_owner", "super_admin", "admin"]) &&
                        < button
                            onClick={() => navigate(`/${companySlug}/assignment/new`)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
                        >
                            + New Assignment
                        </button>
                    }
                </div>
            </div >

            {/* Filters Card */}
            < div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" >
                {/* Search */}
                < div className="mb-4" >
                    <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search by job ID, customer, or supervisor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                    </div>
                </div >

                {/* Status Filters */}
                < div className="space-y-3" >
                    <div className="flex items-center gap-2 flex-wrap">
                        <FiFilter className="text-gray-500 w-4 h-4" />
                        <span className="text-sm font-medium text-gray-700 mr-2">Status:</span>
                        <FilterButton
                            label="All"
                            isSelected={filterStatus === "all"}
                            onClick={() => setFilterStatus("all")}
                            count={assignments.length}
                        />
                        <FilterButton
                            label="Pending Design"
                            isSelected={filterStatus === "pending_design"}
                            onClick={() => setFilterStatus("pending_design")}
                            count={assignments.filter(a => a.main_status === "pending_design").length}
                        />
                        <FilterButton
                            label="In Progress"
                            isSelected={filterStatus === "in_design"}
                            onClick={() => setFilterStatus("in_design")}
                            count={assignments.filter(a => a.main_status === "in_design").length}
                        />
                        <FilterButton
                            label="Ready"
                            isSelected={filterStatus === "ready_for_delivery"}
                            onClick={() => setFilterStatus("ready_for_delivery")}
                            count={assignments.filter(a => a.main_status === "ready_for_delivery").length}
                        />
                        <FilterButton
                            label="Completed"
                            isSelected={filterStatus === "completed"}
                            onClick={() => setFilterStatus("completed")}
                            count={assignments.filter(a => a.main_status === "completed").length}
                        />
                    </div>

                    {/* Job Type Filters */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-700 mr-2">Type:</span>
                        <FilterButton
                            label="All Types"
                            isSelected={filterJobType === "all"}
                            onClick={() => setFilterJobType("all")}
                        />
                        <FilterButton
                            label="Design"
                            isSelected={filterJobType === "Design"}
                            onClick={() => setFilterJobType("Design")}
                        />
                        <FilterButton
                            label="Estimation"
                            isSelected={filterJobType === "Estimation"}
                            onClick={() => setFilterJobType("Estimation")}
                        />
                        <FilterButton
                            label="Design & Estimation"
                            isSelected={filterJobType === "Design & Estimation"}
                            onClick={() => setFilterJobType("Design & Estimation")}
                        />
                        <FilterButton
                            label="Timber"
                            isSelected={filterJobType === "Timber"}
                            onClick={() => setFilterJobType("Timber")}
                        />
                    </div>

                    {/* Sort */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Sort:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="priority">Priority First</option>
                        </select>
                    </div>
                </div >

                {/* Results Count */}
                < div className="mt-4 pt-3 border-t border-gray-200" >
                    <span className="text-sm text-gray-600">
                        Showing <span className="font-semibold text-gray-900">{filteredAssignments.length}</span> of{' '}
                        <span className="font-semibold text-gray-900">{assignments.length}</span> assignments
                    </span>
                </div >
            </div >

            {/* Assignments List */}
            < div className="space-y-3" >
                {
                    filteredAssignments.length === 0 ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiAlertCircle className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No assignments found</h3>
                            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        filteredAssignments.map((job) => (
                            <div
                                key={job.id}
                                onClick={() => handleViewAssignment(job.id)}
                                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer"
                            >
                                <div className="p-4">
                                    {/* Header Row */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-start gap-3 flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-semibold text-gray-900 text-base">
                                                    {job.unique_id}
                                                </h3>
                                                {(job.urgent_quote || job.urgent_design) && (
                                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold flex items-center gap-1">
                                                        <FiFlag className="w-3 h-3" />
                                                        URGENT
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <span className={`px-3 py-1 rounded-md text-xs font-medium border flex items-center gap-1.5 whitespace-nowrap ${getStatusColor(job.main_status)}`}>
                                            {getStatusIcon(job.main_status)}
                                            {job.main_status.replace(/_/g, ' ').toUpperCase()}
                                        </span>
                                    </div>

                                    {/* Content Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                        {/* Customer Info */}
                                        <div className="md:col-span-3">
                                            <div className="flex items-start gap-2">
                                                <FiUser className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {job.customer_name}
                                                    </p>
                                                    <div className="flex items-center gap-1.5 mt-1">
                                                        {job.customer_number && (
                                                            <a
                                                                href={`tel:${job.customer_number}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                                            >
                                                                <FiPhone className="w-3 h-3" />
                                                                Call
                                                            </a>
                                                        )}
                                                        {job.customer_mail && (
                                                            <a
                                                                href={`mailto:${job.customer_mail}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                                                            >
                                                                <FiMail className="w-3 h-3" />
                                                                Email
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Supervisor Info */}
                                        <div className="md:col-span-3">
                                            <div className="flex items-start gap-2">
                                                <FiTool className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {job.carpenter_supervisor_name || "No supervisor"}
                                                    </p>
                                                    {job.carpenter_supervisor_number ? (
                                                        <a
                                                            href={`tel:${job.carpenter_supervisor_number}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 mt-1"
                                                        >
                                                            <FiPhone className="w-3 h-3" />
                                                            Call Site
                                                        </a>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                                            <FiPhoneOff className="w-3 h-3" />
                                                            No contact
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Job Details */}
                                        < div className="md:col-span-3" >
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                                    {job.job_type}
                                                </span>
                                                {getRequiredQuotes(job).map((quote) => (
                                                    <span key={quote} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                                        {quote}
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500">
                                                <FiCalendar className="w-3 h-3" />
                                                {formatDate(job.created_at)}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="md:col-span-3 flex items-center justify-end">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewAssignment(job.id);
                                                }}
                                                className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium flex items-center gap-2"
                                            >
                                                <FiEye className="w-4 h-4" />
                                                View
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div >
                        ))
                    )
                }
            </div >
        </>
    );
};

export default Assignments;